import { FEATURE_FLAGS } from "@/data/globals";
import { numberFromName } from "@/data/numbers";
import {
  GameSystems,
  IDataSpecialRule,
  IDataSpell,
  IQuestSkill,
  ISelectedUnit,
  ISpell,
  IUnit,
  IUpgradeGains,
  RuleCoreType,
  RuleCostFormula,
  RuleGenFormulaParams,
  RuleTargetType,
  SpellType
} from "@/data/types";
import { isFeatureEnabled } from "@/env";
import { mean, uniq, without } from "lodash-es";
import pluralize from "pluralize";
import { Result } from "typescript-result";
import ArmyBookRule from "../armyBook/ArmyBookRule";
import { GameSystemService } from "../GameSystemService";
import { isError, isWeapon, makeCopy } from "../Helpers";
import { ILogger } from "../logging/ILogger";
import createLogger from "../logging/LoggerFactory";
import UnitService from "../UnitService";
import Calculator, { getCostForStat, SPECIAL_SETTINGS, Unit, Weapon } from "./Calculator";

pluralize.addSingularRule(/axes$/i, "axe");
pluralize.addSingularRule(/lens$/i, "lens");

const FORCE_LOG = false;
const DISABLE_LOCAL_LOG = true;
const isLocal = typeof window !== "undefined" && window.location.host.startsWith("localhost");
const isDev = process.env.NODE_ENV === "development";
export const LOG_VERBOSE =
  FORCE_LOG || ((isDev || isLocal) && !DISABLE_LOCAL_LOG) || process.env.LOG_VERBOSE === "true";

const ENABLE_V360 = isFeatureEnabled(FEATURE_FLAGS.V3_6);

const globalAvgMeleeWeapon: Weapon = {
  name: "Average melee weapon",
  range: "melee",
  attacks: 1.5,
  rules: [],
  melee: true
};

const globalAvgRangedWeapon: Weapon = {
  name: "Average ranged weapon",
  range: 18,
  attacks: 1.5,
  rules: [],
  ranged: true
};

export const getAverageModel = (calcSettings: object, skirmish: boolean) => {
  const triple = skirmish && ENABLE_V360;
  const avgMeleeWeapon = calcSettings[SPECIAL_SETTINGS.AverageMeleeWeapon] || globalAvgMeleeWeapon;
  const avgRangedWeapon = calcSettings[SPECIAL_SETTINGS.AverageRangedWeapon] || globalAvgRangedWeapon;
  const skirmishOffensiveMult = calcSettings[SPECIAL_SETTINGS.SKIRMISH_OFFENSIVE_MULT];
  const skirmishDefensiveMult = calcSettings[SPECIAL_SETTINGS.SKIRMISH_DEFENSIVE_MULT];
  return {
    name: "Average unit",
    quality: 4,
    defense: 4,
    speedAdvance: 6,
    speedRush: 6,
    qualityCost: getCostForStat(calcSettings, 4, "quality"),
    rules: triple ? ["Tough"] : [],
    equipment: [
      {
        ...avgMeleeWeapon,
        attacks: triple ? avgMeleeWeapon.attacks * skirmishOffensiveMult : avgMeleeWeapon.attacks
      },
      {
        ...avgRangedWeapon,
        attacks: triple ? avgRangedWeapon.attacks * skirmishOffensiveMult : avgRangedWeapon.attacks
      }
    ],
    tough: triple ? skirmishDefensiveMult : 1,
    normalised: true
  };
};

export default class CalcService {
  private logger: ILogger;
  private LOG_VERBOSE = LOG_VERBOSE;
  /* eslint-disable no-eval */
  public _calc: Calculator; // eslint-disable-line no-eval
  public unitRules: any[];
  public weaponRules: any[];

  public coreUnitRules: string[];
  public coreWeaponRules: string[];

  public _skirmish: boolean = false;
  private _quest: boolean = false;
  private _debug: boolean = false;
  public static DisableBlacklist = false; // isDev;

  private armySpells: ISpell[];

  public get AuraUnitSize(): number {
    const auraSizes = this.calcSettings[SPECIAL_SETTINGS.AuraSizes];

    // OLD, backwards compat
    if (!auraSizes)
      return this._skirmish
        ? this.calcSettings[SPECIAL_SETTINGS._auraSizeSkirmish]
        : this.calcSettings[SPECIAL_SETTINGS._auraSizeBattle];

    // New skirmish aura size
    if (ENABLE_V360) {
      return this._skirmish ? auraSizes["skirmish"] : auraSizes["battle"];
    }
    return this._skirmish ? auraSizes["skirmishHeroes"] : auraSizes["battle"];
  }

  public get AuraSize(): number {
    return this.AuraUnitSize;
  }

  public getAuraMultiplier(unit: Unit, params: RuleGenFormulaParams) {
    return this.getAuraUnitSize(unit, params);
  }

  public getAuraUnitSize(unit: Unit, params: RuleGenFormulaParams) {
    const isAura = params.thisUnitAura || params.otherUnitAura;
    if (!isAura) return 1;

    if (this._skirmish) {
      const auraSizes = this.calcSettings[SPECIAL_SETTINGS.AuraSizes];
      if (!auraSizes) return this.calcSettings[SPECIAL_SETTINGS._auraSizeSkirmish];

      const skirmishAuraSize = auraSizes["skirmish"];

      // v3.6/new
      if (skirmishAuraSize) {
        // "This model and its unit" (Auras)
        if (params?.thisUnitAura) {
          return skirmishAuraSize * auraSizes["skirmishAuraBuffUnitCount"];
        }
        // Anything targeting other units (Buffs)
        else if (params?.otherUnitAura) {
          const targetNumberText = params.assignment?.numberText?.name;
          const targetNumber = targetNumberText ? numberFromName(targetNumberText.replace("up to ", "")) : 0;
          // Aura Unit Size * how many units are affected
          return skirmishAuraSize * targetNumber;
        }
      } else {
        if (params?.thisUnitAura) {
          return auraSizes["skirmishHeroes"];
        }
        // Anything targeting other units
        else if (params.otherUnitAura) {
          const targetNumberText = params.assignment?.numberText?.name;
          const targetNumber = targetNumberText ? numberFromName(targetNumberText.replace("up to ", "")) : 0;
          // number of targets + the aura size
          return auraSizes["skirmishTargeted"] + targetNumber;
        }
      }
    } else {
      // If we're not using "this unit" to calc, then use the generic aura size...
      if (!params?.thisUnit) return this.AuraUnitSize;
      // ...otherwise use a size based off this unit's size
      const result = unit.unitSize * 1.5 + 1.5;
      return result;
    }
  }

  public get initialised(): boolean {
    return this.dataRules?.length > 0;
  }

  public get AverageUnit() {
    return getAverageModel(this.calcSettings, this._skirmish);
  }
  public get avgRangedWeapon() {
    const weapon = this.calcSettings?.[SPECIAL_SETTINGS.AverageRangedWeapon] ?? {};
    const triple = this._skirmish && ENABLE_V360;
    const skirmishOffensiveMult = this.calcSettings[SPECIAL_SETTINGS.SKIRMISH_OFFENSIVE_MULT];

    return {
      ...weapon,
      attacks: triple ? weapon.attacks * skirmishOffensiveMult : weapon.attacks
    };
  }
  public get avgMeleeWeapon() {
    const weapon = this.calcSettings?.[SPECIAL_SETTINGS.AverageMeleeWeapon] ?? {};
    const triple = this._skirmish && ENABLE_V360;
    const skirmishOffensiveMult = this.calcSettings[SPECIAL_SETTINGS.SKIRMISH_OFFENSIVE_MULT];

    return {
      ...weapon,
      attacks: triple ? weapon.attacks * skirmishOffensiveMult : weapon.attacks
    };
  }

  private avgUnitCost: number | null = null;
  public get AverageUnitCost(): number {
    if (this.avgUnitCost !== null) return this.avgUnitCost;
    this.avgUnitCost = this.getUnitCostResult(this.AverageUnit).value;
    return this.avgUnitCost;
  }

  private safeWeaponRules: string[] = []; // ["AP", "Blast", "Deadly"];
  private _scriptBlacklist: string[] = [];
  private _scriptBlacklistRegexes: RegExp[] = [];
  private setBlacklist() {
    const ruleNames = this.dataRules?.map((x) => x.name) ?? [];
    const globalProps = Object.getOwnPropertyNames(typeof window === "undefined" ? global : window);
    const blacklist = CalcService.DisableBlacklist
      ? []
      : without(
          [...globalProps],
          ...ruleNames,
          "require",
          "constructor",
          "while(",
          "for(",
          "Math",
          "parseInt",
          "addRuleTo",
          "weap",
          "_",
          "Range",
          "Lock",
          "nd",
          "name",
          "top",
          "stop",
          "Screen",
          "interpolate",
          "clamp",
          isLocal || isDev ? "console" : undefined
        );

    this._scriptBlacklist = blacklist;
    // Word-boundary match so e.g. "Set" doesn't flag "calcSettings"
    this._scriptBlacklistRegexes = blacklist.map(
      (word) => new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`)
    );
  }

  constructor(
    public calcSettings: object,
    public dataRules?: IDataSpecialRule[],
    private units?: IUnit[],
    public gameSystemId?: number,
    public dataSpells: IDataSpell[] = [],
    armySpells: ISpell[] = []
  ) {
    Object.assign(this, this.calcSettings);

    this.logger = createLogger(true);
    if (!dataRules) {
      dataRules = [];
    }
    this._skirmish = [
      GameSystems.gff,
      GameSystems.aofs,
      GameSystems.aofq,
      GameSystems.aofqai,
      GameSystems.gfsq,
      GameSystems.gfsqai
    ].includes(this.gameSystemId);

    this._quest = GameSystemService.isQuest(GameSystemService.findById(gameSystemId));

    // Assign aliases
    for (let i = 0; i < dataRules.length; i++) {
      const rule = dataRules[i];
      if (rule.aliasedRuleId && !rule.costFormula) {
        const parent = dataRules.find((x) => x.id == rule.aliasedRuleId);
        if (!parent) {
          // console.error('Cannot find parent rule for', rule);
        }
        // Re-assign alias
        dataRules[i] = {
          ...parent,
          id: rule.id,
          name: rule.name,
          coreType: null
        };
      }
    }

    this.setBlacklist();

    this.unitRules = dataRules
      .filter((r) => r.targetType === RuleTargetType.Unit)
      .map((x) => this.parseRule(x));
    this.weaponRules = dataRules
      .filter((r) => r.targetType === RuleTargetType.Weapon)
      .map((x) => this.parseRule(x));

    this.coreUnitRules = dataRules
      .filter((r) => r.targetType === RuleTargetType.Unit && r.coreType === RuleCoreType.Core)
      .map((x) => x.name);
    this.coreWeaponRules = dataRules
      .filter((r) => r.targetType === RuleTargetType.Weapon && r.coreType === RuleCoreType.Core)
      .map((x) => x.name);

    this._calc = new Calculator(
      this.calcSettings,
      this.unitRules,
      this.weaponRules,
      this.units ?? [],
      this._skirmish
    );
    this._calc.calcService = this;

    this.setArmySpells(armySpells);

    if (typeof window !== "undefined") {
      (window as any).CalcService = this;
    }
  }

  public setUnits(units: IUnit[]) {
    this.units = units;
    this._calc.setUnits(units);
  }

  public setDataSpells(dataSpells: IDataSpell[]) {
    this.dataSpells = dataSpells;
    //this._calc.setDataSpells(dataSpells);
  }

  public setArmySpells(armySpells: ISpell[]) {
    this.armySpells = armySpells.map((x) => this.parseRule(x)) as ISpell[];
    const spellRules = this.armySpells.map((x) => ({ ...x, name: "spell_" + x.name }));
    this.unitRules = this.unitRules.filter((x) => !x.name.startsWith("spell_")).concat(spellRules);
    this._calc.setUnitRules(this.unitRules);
  }

  public setQuestSkills(questSkills: IQuestSkill[]) {
    const questSkillRules = questSkills
      .map((x) => ({ ...x, name: "questSkill_" + x.name }))
      .map((x) => this.parseRule(x));

    this.unitRules = this.unitRules.filter((x) => !x.name.startsWith("questSkill_")).concat(questSkillRules);
    this._calc.setUnitRules(this.unitRules);
  }

  public parseRule(rule: {
    name: string;
    hasRating?: boolean;
    woundFormula?: string;
    rangedWoundFormula?: string;
    costFormula?: RuleCostFormula[];
    targetType?: RuleTargetType;
  }) {
    try {
      // COPY the rule...
      rule = JSON.parse(JSON.stringify(rule));

      const evalWoundFormula = (formula) =>
        eval(
          `(function(x, ${rule.targetType === RuleTargetType.Unit ? "unit" : "weapon"}) { ${
            formula.includes("return") ? "" : "return "
          }${formula} })`
        ).bind(this);

      if (rule.woundFormula) rule.woundFormula = evalWoundFormula(rule.woundFormula);
      if (rule.rangedWoundFormula) rule.rangedWoundFormula = evalWoundFormula(rule.rangedWoundFormula);

      if (rule.costFormula) {
        rule.costFormula = this.parseCostFormula(rule);
        if (rule.targetType === RuleTargetType.Weapon) {
          const isRecursive = rule.costFormula.some((cf) => /get(?:Safe)?WeaponCost/.test(cf.value));
          const isSafe = !isRecursive; // && !hasApplyRuleCost;
          if (isSafe) {
            this.safeWeaponRules.push(rule.name);
            //console.log(rule.name + " is safe");
          }
        }
      }
    } catch (e) {
      console.error(e, rule);
    }

    return rule;
  }

  public parseCostFormula(rule: {
    costFormula?: RuleCostFormula[];
    name: string;
    hasRating?: boolean;
    targetType?: RuleTargetType;
  }) {
    if (!rule.costFormula) return [];

    let costFormula = rule.costFormula as any[];
    // If it's the old object format, then convert to new array format
    if (!Array.isArray(costFormula)) {
      costFormula = Object.keys(costFormula).map((x) => ({
        type: x,
        value: costFormula[x]
      }));
    }

    // "New" format each formula is an array entry
    for (const formula of costFormula) {
      //if (rule.name === "Infiltrate") debugger;

      formula.fn = this.parseCost(
        formula.value || "0",
        rule.name,
        formula.type,
        rule.hasRating,
        rule.targetType,
        formula.params,
        formula.multiplier
      );
    }

    return costFormula;
  }

  public parseCost(
    fnStr: string,
    name: string,
    definitionType: string = "",
    ruleHasRating: boolean = false,
    targetType: RuleTargetType = RuleTargetType.Unit,
    params?: RuleGenFormulaParams,
    multiplier?: number
  ) {
    if (typeof fnStr === "string") {
      const definitions = targetType === RuleTargetType.Weapon ? costWeaponFormulas : costUnitFormulas;
      const definition = definitions.find((x) => x.name === definitionType);
      const fnArgs = definition?.arguments || [];
      const fnReturnType = definition?.returnType || "number";
      const hasUnitArg = fnArgs.includes("unit");
      const hasBaseUnitArg = fnArgs.includes("baseUnit");
      const hasWeaponArg = fnArgs.includes("weapon");
      const hasRating =
        ruleHasRating &&
        ((targetType === RuleTargetType.Weapon && hasWeaponArg) ||
          (targetType === RuleTargetType.Unit && hasUnitArg));
      const target = targetType === RuleTargetType.Weapon ? "weapon" : "unit";
      const returnStatement = fnStr.includes("return") || fnReturnType === "void" ? "" : "return ";
      const log = (text: string) => (this.LOG_VERBOSE ? text : "");
      const hasMultiplier = typeof multiplier === "number";

      const auraLogic = `
        const checkUnit = ${hasBaseUnitArg ? "baseUnit" : "unit"};
        const upgradeSingleInUnit = checkUnit.models === 1 && checkUnit.unitSize > 1;
        if (!this._skirmish && params.thisUnitAura && upgradeSingleInUnit) { params.thisUnit = true; params.avgUnit = false; }
      `;

      const fnScript = `
        const ruleName=\`${name}\`;
        ${hasWeaponArg ? `const safeWeapon = this.strip(weapon, ruleName);` : ""}
        try {
          ${log(`console.group("${name}" + " formula:");`)}
          ${params ? `const params = ${JSON.stringify(params)};` : ""}
          ${hasRating ? `const rating = ${target}?.[ruleName?.toLowerCase()] || 0;` : ""}
          ${hasUnitArg && params ? auraLogic : ""}
          const result = (() => {
            ${returnStatement}${fnStr}
          })();
          ${log(`console.log(ruleName + " result", result);console.groupEnd();`)}
          ${
            params && hasMultiplier
              ? `if (typeof(result) === "number") {
                const isEnemy = params.assignment?.["friendly-enemy"]?.name === "enemy";
                const auraSizeMult = this.getAuraMultiplier(unit, params);
                const finalMultiplier = ${multiplier} * auraSizeMult;
                const finalResult = result * finalMultiplier * ((isEnemy && result < 0) ? -1 : 1);
                ${log(`console.log(ruleName + " result", { mult: finalMultiplier, isEnemy, finalResult });`)}
                return finalResult;
              }`
              : ``
          }
          return result;
        } catch (fe) {
          console.error(ruleName + " FUNCTION ERROR", fe);debugger;
          ${log(`console.groupEnd();`)}
          throw fe;
        }`;

      try {
        const inputTest = fnStr
          .replace(/\/\/.+(?:$|\n)/gm, "") // Remove comments
          .replace(/\s+/g, " "); // Normalize whitespace (keep word boundaries intact)
        const bannedWords = this._scriptBlacklist.filter((_, i) =>
          this._scriptBlacklistRegexes[i].test(inputTest)
        );
        if (bannedWords.length > 0) {
          throw `Input ${name} contains blacklisted words: ` + bannedWords.join(", ");
        }

        return Function(...fnArgs, fnScript).bind(this);
      } catch (e) {
        console.warn(`[${name}] Could not eval ${name} '${fnScript}' -> `, e.message || e);
      }
    } else {
      return eval(`() => ` + JSON.stringify(fnStr));
    }
  }

  public getUnitCostResult(unit: Unit) {
    if (this._debug) console.debug("getUnitCost", unit);
    const costResult = this._calc.unitCost(unit);
    if (costResult.ok) {
      return Result.ok(costResult.value * this.calcSettings[SPECIAL_SETTINGS.GLOBAL_MULTIPLIER]);
    } else {
      return costResult;
    }
  }

  public getUnitCost(unit: Unit, mutateUnit = false) {
    try {
      if (this._debug) console.debug("getUnitCost", unit);
      const costResult = this._calc.unitCost(unit, mutateUnit);
      if (isError(costResult)) throw costResult.error;
      return costResult.value * this.calcSettings[SPECIAL_SETTINGS.GLOBAL_MULTIPLIER];
    } catch (e) {
      console.error("getUnitCost error", e);
      return 99999;
    }
  }

  public getWeaponCost(weapon: any, unit: any) {
    if (this._debug) console.debug("getWeaponCost", weapon);
    if (weapon.range === "melee" && !weapon.melee) weapon.melee = true;
    const costResult = this._calc.weaponCost(weapon, unit);
    if (isError(costResult)) throw costResult.error;
    return costResult.value;
  }

  //#region Formula Helpers

  public copy = (x) => makeCopy(x);
  // Remove rule from weapon to avoid recursion
  public cloneWithout = (x, rule) =>
    this.copy({
      ...x,
      rules: x.rules?.filter((x) => x !== rule) ?? []
    });
  public strip = (x, rule) => this.cloneWithout(x, rule);

  public getUnitBaseCost(unit: Unit) {
    if (this._debug) console.debug("getUnitBaseCost", unit);
    const result = this._calc.unitBaseCost(unit);
    if (isError(result)) throw result.error;
    return result.value;
  }

  public getMoraleCost(unit: Unit) {
    if (this._debug) console.debug("unitMoraleCost", unit);
    return this._calc.unitMoraleCost(unit);
  }

  public getMoraleStatCost(unit: Unit) {
    if (this._debug) console.debug("unitMoraleStatCost", unit);
    return this._calc.unitMoraleStatCost(unit);
  }

  public getDefenseCost(unit: Unit) {
    if (this._debug) console.debug("unitDefenseCost", unit);
    return this._calc.unitDefenseCost(unit);
  }

  public getUnitCostDiff(unit: any, modifier: any, rule: string) {
    let normalised: Unit = unit.tough ? unit : CalcService.normalizeUnit(unit);
    let modifiedUnit = { ...normalised, ...modifier, rules: normalised.rules.concat(modifier.rules || []) };

    if (rule) {
      normalised = this.strip(normalised, rule);
      modifiedUnit = this.strip(modifiedUnit, rule);
    }

    const costResultModified = this._calc.unitCost(modifiedUnit);
    const costResultNormalised = this._calc.unitCost(normalised);

    if (isError(costResultModified)) throw costResultModified.error;
    if (isError(costResultNormalised)) throw costResultNormalised.error;

    return costResultModified.value - costResultNormalised.value;
  }

  public getUnitBaseCostDiff(unit: any, modifier: any) {
    const resultA = this._calc.unitBaseCost({ ...unit, ...modifier });
    const resultB = this._calc.unitBaseCost(unit);

    if (isError(resultA)) throw resultA.error;
    if (isError(resultB)) throw resultB.error;

    return resultA.value - resultB.value;
  }

  public getUnitRuleCost(originalUnit: Unit, rule: string, rating: any) {
    const unit = makeCopy(originalUnit);
    if (this._debug) console.debug("getUnitRuleCost", unit);
    if (!unit.rules) unit.rules = [];
    // Add the rule to the unit if it isn't there
    if (!unit.rules.find((x) => x === rule)) {
      unit.rules.push(rule);
      if (rating) {
        CalcService.applyRating(unit, { name: rule, rating });
      }
    }
    const result = this._calc.unitRuleCost(unit, rule);
    if (isError(result)) throw result.error;
    return result.value;
  }

  public getRuleCostUnit(ruleName: string, formula: string) {
    const rule = this._calc.unitRules[ruleName];
    if (!rule) return 9999;
    return rule.formula.find((x) => x.type === (formula || "cost"))?.fn;
  }

  public getSpeed(unit: Unit, values: { advance?: number; rush?: number; single?: number }) {
    return this._calc.speedCost(unit, values);
  }

  public getFlyOverEffectMult(unit: Unit) {
    const { FlyOverEffect, FlyOverExponent } = this.calcSettings as any;
    const moveDistance = Math.min(18, Math.max(unit.speedAdvance, unit.speedRush));

    // so 1.25 would become 0.25
    const multiplierPart = FlyOverEffect - 1;
    const speedPart = Math.pow(moveDistance / 3, FlyOverExponent);

    return 1 + multiplierPart * speedPart;
  }

  public getPassThroughEffectMult(unit: Unit) {
    const { PassThroughEffect, PassThroughExponent } = this.calcSettings as any;
    const moveDistance = Math.min(18, Math.max(unit.speedAdvance, unit.speedRush));

    const speedPart = Math.pow(moveDistance / 3, PassThroughExponent);
    return PassThroughEffect * speedPart;
  }

  public getSafeWeaponCost(inputWeapon: any, unit: any) {
    const weapon = makeCopy(inputWeapon);
    // TEMP fix for Bane basically, need to figure that one out...
    if (unit.name !== "Average unit") {
      weapon.rules = weapon.rules.filter((r) => this.safeWeaponRules.includes(r));
    }
    return this.getWeaponCost(weapon, unit);
  }

  public getWeaponBaseCost(weapon: any) {
    if (this._debug) console.debug("getWeaponBaseCost", weapon);
    if (weapon.range === "melee" && !weapon.melee) weapon.melee = true;
    const result = this._calc.weaponBaseCost(weapon);
    if (isError(result)) throw result.error;
    return result.value;
  }

  public getEffectiveAP(weapon: Weapon & { ap?: number }, unit: Unit) {
    let ap = weapon.ap ?? 0;
    // for (const ruleName of weapon.rules) {
    //   const rule = this.dataRules.find((x) => x.name === ruleName);
    //   if (rule) {
    //     //console.log(rule.name, rule);
    //     const ruleApMatch = rule.description.match(/gets? AP\(\+?(\d+)\)/i);
    //     if (ruleApMatch) {
    //       const ruleAp = parseInt(ruleApMatch[1], 10);
    //       //ap += ruleAp;
    //     }
    //   }
    // }
    // for (const ruleName of unit.rules) {
    //   const rule = this.dataRules.find((x) => x.name === ruleName);
    //   if (rule) {
    //   }
    // }
    const result = Math.min(5, ap);
    return result;
  }

  public getRuleCostWeapon(ruleName: string, formula: string) {
    const rule = this._calc.weaponRules[ruleName];
    if (!rule) return 9999;
    return rule.formula.find((x) => x.type === (formula || "cost"))?.fn;
  }

  public getRuleFormula(ruleName: string, formula?: string) {
    const rule = this._calc.unitRules[ruleName] || this._calc.weaponRules[ruleName];
    const result = rule.formula.find((x) => x.type === (formula || "cost"))?.fn || rule.formula[0]?.fn;
    return result;
  }

  public getWeaponCostDiff(weapon, unit, ruleName, modifier) {
    if (LOG_VERBOSE) {
      console.group(`getWeaponCostDiff ${ruleName}`);
    }
    const weaponDataRules = this.dataRules.filter(
      (x) => x.targetType === RuleTargetType.Weapon && !x.ruleType
    );
    const allowedRules = weaponDataRules
      .filter((x) => {
        const costKeys = Object.keys(x.costFormula || {});
        return costKeys.length === 1 && costKeys[0] === "baseCostMultiplier";
      })
      .map((x) => x.name);

    const baseWeapon = this.cloneWithout(weapon, ruleName);
    // Remove any rules that will cause recursive issues or clash
    baseWeapon.rules = baseWeapon.rules.filter((x) => allowedRules.includes(x));

    const rules = modifier.rules
      ? uniq((baseWeapon.rules ?? []).concat(modifier.rules))
      : (baseWeapon.rules ?? []);

    const modified = makeCopy({ ...baseWeapon, ...modifier, rules });
    // If modifier has AP but rule isn't present
    if (modifier.ap && modified.rules.some((x) => x === "AP") === false) {
      modified.rules.push("AP");
    }
    if (LOG_VERBOSE) {
      console.group(`original unit`);
    }
    const originalCost = this.getSafeWeaponCost(baseWeapon, unit);
    if (LOG_VERBOSE) {
      console.log("originalCost", originalCost.toFixed(2));
      console.groupEnd();
    }
    if (LOG_VERBOSE) {
      console.group(`modified unit`);
    }
    const modifiedCost = this.getSafeWeaponCost(modified, unit);
    if (LOG_VERBOSE) {
      console.log("modifiedCost", modifiedCost.toFixed(2));
      console.groupEnd();
    }

    if (LOG_VERBOSE) {
      console.log({
        weapon,
        modified,
        final: (modifiedCost - originalCost).toFixed(1)
      });
      console.groupEnd();
    }

    return modifiedCost - originalCost;
  }

  public getWeaponBaseCostDiff(weapon, rule, modifier) {
    const modified = this.cloneWithout({ ...weapon, ...modifier }, rule);
    // If modifier has AP but rule isn't present
    if (modifier.ap && modified.rules.some((x) => x === "AP") === false) {
      modified.rules.push("AP");
    }
    const modifiedCost = this.getWeaponBaseCost(modified);
    const originalCost = this.getWeaponBaseCost(this.cloneWithout(weapon, rule));

    return modifiedCost - originalCost;
  }

  public getWeaponCostOnSix(weapon: Weapon, unit: Unit, ruleName: string) {
    var safeWeapon = this.strip(weapon, ruleName);
    var baseCost = this.getWeaponCost(safeWeapon, { quality: unit.quality });
    var betterCost = this.getWeaponCost(safeWeapon, { quality: unit.quality - 1 });
    return betterCost - baseCost;
  }

  public getAutoWoundCost() {
    const weapon = this.calcSettings[SPECIAL_SETTINGS.AutoWoundWeapon];
    const cost = this.getWeaponCost(weapon, {
      quality: parseInt(this.calcSettings[SPECIAL_SETTINGS.AutoWoundQuality])
    });
    return cost;
  }

  public hitsQualityMult(params: { trigger: any; effect: any }, unit: Unit) {
    const param = params.effect.ofSixOrOne || params.trigger?.ofSixOrOne;
    const paramName = param?.name ?? "";
    if (/of 6/.test(paramName)) return 1 / (7 - unit.quality);
    if (/of 1/.test(paramName)) return (7 - unit.quality) / 6;
    return 1;
  }
  /* @deprecated */
  public dealHitsMult(params: { trigger: any; effect: any }, unit: Unit) {
    return this.hitsQualityMult(params, unit);
  }

  public getAttackSuccessChance(unit: Unit) {
    return (7 - unit.quality) / 6;
  }

  public dealWoundsMult(params: { trigger: any; effect: any }, unit: Unit) {
    const param = params.effect.ofSixOrOne || params.trigger?.ofSixOrOne;
    const paramName = param?.name ?? "";
    if (/of 6/.test(paramName)) return 1 / 6; // Don't care about quality, it just happens on a 6
    if (/of 1/.test(paramName)) return (7 - unit.quality) / 6;
    return 1;
  }

  public ofOneToBlockMult(params: { trigger: any; effect: any }, unit: Unit) {
    const param = params.effect.ofSixOrOne || params.trigger?.ofSixOrOne;
    if (param?.name === "of 1 to block hits") {
      return this.getAttackSuccessChance(unit);
    }
    return 1;
  }

  public applyWeaponRule(weapon, params) {
    const weaponRule = params.effect.weaponRule;
    if (!weaponRule) return weapon;
    if (!weapon.rules.includes(weaponRule.name)) weapon.rules.push(weaponRule.name);
    this.applyRating(weapon, { name: weaponRule.name, rating: weaponRule.rating });
    return weapon;
  }

  public getLibraryUnit(unitName: string) {
    return this.units?.find((x) => x.name === unitName);
  }

  public getLibraryUnitByRule(unit, ruleName) {
    const unitNameInput = unit[ruleName.toLowerCase()];
    const unitName = unitNameInput.indexOf("[") > 0 ? /(.+)\s\[/.exec(unitNameInput)?.[1] : unitNameInput;
    if (unit.name === unitName) throw "Recursive unit";
    const unitNameSingular = pluralize(unitName, 1, false);
    const libraryUnit = this.units?.find((x) => x.name === unitName || x.name === unitNameSingular);
    return libraryUnit;
  }

  //#endregion

  public getBoostMultiplier(name: string, multiplier?: number) {
    // Get the rule we're trying to boost
    const baseRule = this.unitRules.find((x) => x.name === name);
    // If we don't find it, just return the name that was passed in
    if (!baseRule) return { name, multiplier };

    const formula = baseRule.costFormula.find((x) => x.type === "applyRuleCost");
    // If rule being referenced rule doesn't use "Rule Cost Multiplier" then just apply that rule directly
    if (!formula) return { name, multiplier };

    try {
      // Execute the boosted rule's formula so we can read the output
      const result = formula.fn();
      // If the multiplier is > 1
      if (!result.multiplier || result.multiplier >= 1) return { name };
      // Get the "other half" of the boosted rule's multiplier
      const multiplier = 1 - result.multiplier;

      return { name: result.name, multiplier };
    } catch (e) {
      return { name };
    }
  }

  public getDefensiveToughMult(unit: Unit) {
    return unit.tough / (this._skirmish ? this.calcSettings[SPECIAL_SETTINGS.SKIRMISH_DEFENSIVE_MULT] : 1);
  }

  static round(preciseCost, factor = 5) {
    return Math.round(preciseCost / factor) * factor;
  }

  /**
   * @deprecated - Transition to calculateUnitCostResult
   */
  public calculateUnitCost(unit: IUnit) {
    // if (LOG_VERBOSE) {
    //   console.group("calculateUnitCost");
    // }
    const calculatableUnit = CalcService.normalizeUnit(CalcService.formatUnit(unit));
    this.logger?.log("calculatableUnit", calculatableUnit);

    const result = this.getUnitCost(calculatableUnit);

    // if (LOG_VERBOSE) {
    //   console.groupEnd();
    // }
    return result;
  }

  public calculateUnitCostResult(unit: IUnit) {
    // if (LOG_VERBOSE) {
    //   console.group("calculateUnitCost");
    // }
    const calculatableUnit = CalcService.normalizeUnit(CalcService.formatUnit(unit));
    this.logger?.log("calculatableUnit", calculatableUnit);

    const result = this.getUnitCostResult(calculatableUnit);

    // if (LOG_VERBOSE) {
    //   console.groupEnd();
    // }
    return result;
  }

  private spellsCost: number | null = null;

  public getSpellCosts() {
    if (this.spellsCost !== null) return this.spellsCost;

    const costs = [];
    for (const spell of this.armySpells || []) {
      // Custom Spell
      if (!spell.spellbookId && !spell.generation) {
        costs.push(this.calcSettings[SPECIAL_SETTINGS.AVG_SPELL_COST]);
        // TODO
        continue;
      }
      costs.push(this.getSpellCost(spell));
    }
    const avgCost = mean(costs.length > 0 ? costs : [this.calcSettings[SPECIAL_SETTINGS.AVG_SPELL_COST]]);
    return (this.spellsCost = avgCost);
  }

  public getSpellCost(spell: ISpell, skirmish: boolean = false) {
    if (spell.generation) {
      const attackSpell = spell.type === SpellType.Attack;

      const unitWithRule = makeCopy({
        ...this.AverageUnit,
        rules: [`spell_${spell.name}`]
      });

      const unitWithRuleCost = this.getUnitCost(unitWithRule);
      const diff = unitWithRuleCost - this.AverageUnitCost;
      const spellSettings = this.calcSettings?.[SPECIAL_SETTINGS.Spells] || {};
      const thresholdMult = ENABLE_V360
        ? spellSettings[`lvl${spell.threshold}_mult`]
        : attackSpell
          ? 1
          : (1 / 6) * (4 - spell.threshold);

      const result = diff * thresholdMult;
      return result;
    } else {
      const dataSpell = this.dataSpells.find((x) => x.id === spell.spellbookId);
      // Custom spells?
      if (!dataSpell) {
        console.log(spell.costFormula);
        try {
          const formula = spell.costFormula[0];
          if (formula) {
            const fn = this.parseCost(formula.value, spell.name);
            const value = parseInt(fn());
            return value;
          }
          return this.calcSettings[SPECIAL_SETTINGS.AVG_SPELL_COST];
        } catch (e) {
          console.error("Spell custom formula error", e);
        }
        return this.calcSettings[SPECIAL_SETTINGS.AVG_SPELL_COST];
      }
      const key = this._skirmish || skirmish ? "costsSkirmish" : "costs";
      const formulaStr = dataSpell[key][spell.threshold];
      try {
        const formula = this.parseCost(formulaStr, dataSpell.name);
        const value = parseInt(formula());
        return value;
      } catch (e) {
        console.error(e);
        return 999999;
      }
    }
  }

  public getRuleTags(ruleName: string) {
    const rule = this.dataRules.find((x) => x.name === ruleName);
    return rule?.tags || [];
  }

  //#region Normalise Unit/Weapon

  public static formatUnit(unit: IUnit) {
    return makeCopy({
      ...unit,
      weapons: unit.weapons.map((x) => ({
        ...x,
        count: x.count ? x.count * unit.size : unit.size
      })),
      items: (unit.items || []).map((item) => ({
        ...item,
        count: item.count ? item.count * unit.size : unit.size,
        content: item.content.map((c) => {
          if (!isWeapon(c)) return c;
          return { ...c, count: c.count ? c.count * unit.size : unit.size };
        })
      }))
    });
  }

  static normalizeWeapon(weapon) {
    if (weapon === undefined) {
      return undefined;
    }
    try {
      const specialRules = weapon.specialRules
        .map((sr) => {
          if (typeof sr === "string") {
            return ArmyBookRule.FromString(sr);
          }
          return sr;
        })
        .filter((sr) => Boolean(sr));

      const result = {
        name: weapon.name || weapon.label,
        range: weapon.range > 0 ? weapon.range : "melee",
        melee: !(weapon.range > 0),
        ranged: weapon.range > 0,
        attacks: weapon.attacks,
        rules: specialRules.map((sr) => sr.name),
        assault: specialRules.some((x) => x.name === "Assault")
      };

      specialRules.forEach((sr) => this.applyRating(result, sr));

      return result;
    } catch (e) {
      debugger;
      throw e;
    }
  }

  static normalizeUnit(unit: IUnit | ISelectedUnit): Unit {
    if (unit === undefined) {
      return undefined;
    }

    // Already normalised
    if ((unit as any).normalised) return unit as any as Unit;

    // If this is an upgraded unit, use its loadout
    const loadout =
      (unit as ISelectedUnit).loadout || (unit.weapons as IUpgradeGains[]).concat(unit.items || []);
    const allEquipment = UnitService.getAllEquipment({ loadout });
    const weapons = allEquipment.filter((x) => isWeapon(x));

    // Explode equipment out into individual entries
    let equipment = [];
    for (const weapon of weapons) {
      for (let i = 0; i < (weapon.count || 1); i++) {
        equipment.push(this.normalizeWeapon(weapon));
      }
    }

    const calculatableUnit = {
      name: unit.name,
      models: unit.size,
      unitSize: unit.fullSize || unit.size,
      quality: unit.quality,
      defense: unit.defense,
      rules: [],
      equipment,
      tough: 0,
      normalised: true,
      speedAdvance: 6,
      speedRush: 6
    };

    const upgradeRules = UnitService.getAllUpgradedRules({
      loadout,
      selectedUpgrades: (unit as any).selectedUpgrades ?? []
    } as ISelectedUnit);
    const rules = unit.rules.concat(upgradeRules);
    calculatableUnit.rules = uniq(rules.map((sr) => sr.name));
    rules.forEach((sr) => this.applyRating(calculatableUnit, sr));

    if (!calculatableUnit.tough) {
      calculatableUnit.tough = 1;
    }

    return calculatableUnit;
  }

  public applyRating(x: any, rule: { name: string; rating?: any }) {
    CalcService.applyRating(x, rule);
  }

  static applyRating(x, rule: { name: string; rating?: any }) {
    if (rule.rating) {
      const ruleKey = rule.name.toLowerCase();
      const key = ruleKey === "defense" ? "improvedDefense" : ruleKey;
      const numRating = parseFloat(rule.rating);
      const isNumericRating = !isNaN(numRating);
      const isArmourRule = ruleKey === "armor";
      if (isArmourRule) {
        const existingValue = x[key] || 999;
        x[key] = isNumericRating ? Math.min(existingValue, numRating) : rule.rating;
      } else {
        x[key] = isNumericRating ? (x[key] || 0) + numRating : rule.rating;
      }
    }
  }

  public applyAP(weapon: Weapon, rating: number) {
    if (!weapon.rules.includes("AP")) {
      weapon.rules.push("AP");
    }
    CalcService.applyRating(weapon, { name: "AP", rating });
  }

  //#endregion
}

export const costUnitFormulas = [
  {
    name: "cost",
    label: "Cost (per model)",
    arguments: ["unit"],
    returnType: "number",
    hint: 'Either a number or a function ((unit) => number) that should return a number which is the cost of applying the rule to the unit. Note that the unit passed in will have an additional property on it called "qualityCost" and "defenseCost" which are the cost in points for the unit\'s quality and defense.'
  },
  {
    name: "unitCost",
    label: "Unit Cost (cost for the whole unit)",
    arguments: ["unit"],
    returnType: "number",
    hint: "A number or function ((unit) => number) that will be passed the unit and returns a number that will be added to the total cost for the unit. This happens after the total per unit is multiplied by the number of models, so it is used to add a flat cost to a unit, regardless of how many models it has."
  },
  {
    name: "baseCostMultiplier",
    label: "Base cost multiplier",
    arguments: ["unit"],
    returnType: "number",
    hint: "A number, or function ((unit) => number). Returns a number, used to multiply the base cost."
  },
  {
    name: "addRules",
    label: "Add Rules",
    arguments: [],
    returnType: "object",
    hint: "An array of rule names to add to the unit. This happens first thing, so any rules added will be able to use any of the following attributes."
  },
  {
    name: "applyRuleCost",
    label: "Apply Rule Cost",
    arguments: ["unit", "baseUnit"],
    returnType: "object",
    hint: `A function ((unit, baseUnit) => object) that will be passed the unit and the base/original unit and returns an object specifying the rule and the multiplier { name: "Ambush", multiplier: 0.75 }`
  },
  {
    name: "applyRuleCostWeapon",
    label: "Apply Rule Cost (Weapon)",
    arguments: ["weapon", "unit"],
    returnType: "object",
    hint: `A function ((weapon, unit) => object) that will be passed the weapon and unit and returns an object specifying the rule and the multiplier { name: "AP", rating: 1, multiplier: 0.75 }`
  },
  {
    name: "properties",
    label: "Modify Unit",
    arguments: ["unit"],
    returnType: "void",
    hint: "A function ((unit) => void) that gets passed the unit and can adjust the properties (e.g. quality, defense). It doesn't need to return anything, just modify the unit."
  },
  {
    name: "unitPropertiesForWeapons",
    label: "Unit properties for that weapon",
    arguments: ["unit", "weapon"],
    returnType: "void",
    hint: "A function ((unit, weapon) => void) that can modify the unit temporarily while calculating each of its weapons. Will be passed both the unit and the weapon so it can decide whether to touch the weapon or not."
  },
  {
    name: "modifyWeapons",
    label: "Modify weapons",
    arguments: ["weapon"],
    returnType: "void",
    hint: "A function ((weapon) => void) that will be applied to each of the unit's weapons one by one. It will thus have a chance to make changes to each individual weapon. Applied before weapon's base cost is computed, so any changes made here will affect all of the weapon cost calculations."
  },
  {
    name: "weaponBaseCostMultiplier",
    label: "Weapon base cost multiplier",
    arguments: ["weapon"],
    returnType: "number",
    hint: "A number or function ((weapon) => number) by which to multiply the base cost, or a function that returns a number to multiply by. If a function, it will be passed the weapon. This happens just after the weapon's cost is multiplied by the unit's quality."
  },
  {
    name: "weaponBaseCost",
    label: "Weapon base cost",
    arguments: ["weapon"],
    returnType: "number",
    hint: "A number or function ((weapon) => number). Returns a flat cost which will be added to each weapon's base cost."
  },
  {
    name: "weaponCost",
    label: "Weapon cost",
    arguments: ["weapon", "unit"],
    returnType: "number",
    hint: "A number or function ((weapon, unit) => number). Returns a flat cost which will be added to each weapon."
  },
  {
    name: "initialise",
    label: "Initialise",
    arguments: ["unit"],
    returnType: "void",
    hint: "A function ((unit) => void). Modify the unit before anything happens."
  }
];

// addRules
// cost
// modifyWeapons
// properties
// unitCost
//unitPropertiesForWeapons
// weaponBaseCostMultiplier

export const costWeaponFormulas = [
  // {
  //   name: 'addRules',
  //   label: 'Add Rules',
  //   arguments: [],
  //   hint: 'An array of rule names that will be added to the weapon for purposes of calculating its cost.',
  // },
  // {
  //   // NOTE: not used ATM
  //   name: 'addUnitRules',
  //   label: 'Add unit rules',
  //   arguments: ['unit'],
  //   hint: 'An array of rule names that will be added to the weapon (UNIT???) for purposes of calculating its cost.'
  // },
  {
    name: "cost",
    label: "Cost",
    arguments: ["weapon", "unit"],
    returnType: "number",
    hint: "A number or a function ((weapon, unit) => number). Returns a flat cost which is added after all other calculations."
  },
  {
    name: "weaponBaseCost",
    label: "Base cost",
    arguments: ["weapon"],
    returnType: "number",
    hint: "A number or function ((weapon) => number). Returns a flat cost which will be added to the weapon's base cost."
  },
  {
    name: "baseCostMultiplier",
    label: "Base cost multiplier",
    arguments: ["weapon"],
    returnType: "number",
    hint: "A number, or function ((weapon) => number). Returns a number, used to multiply the base cost."
  },
  {
    name: "newQuality",
    label: "Quality override",
    arguments: ["quality", "weapon"],
    returnType: "number",
    hint: "A function ((quality: number, weapon) => number). Passed the original quality of the unit holding the weapon and should return a number which will be used as the quality of the unit when calculating the weapon cost."
  },
  {
    name: "addRules",
    label: "Add Rules",
    arguments: [],
    returnType: "object",
    hint: "An array of rule names to add to the unit. This happens first thing, so any rules added will be able to use any of the following attributes."
  },
  {
    name: "weaponProperties",
    label: "Properties (aka weapon stats)",
    arguments: ["weapon"],
    returnType: "void",
    hint: "A function ((weapon) => void) that gets passed the weapon and can adjust the properties (e.g. attacks, ap) while calculating the cost. It doesn't need to return anything, just modify the weapon."
  },
  {
    name: "applyRuleCost",
    label: "Apply Rule Cost",
    arguments: ["weapon", "unit"],
    returnType: "object",
    hint: `A function ((weapon, unit) => object) that will be passed the weapon and unit and returns an object specifying the rule and the multiplier { name: "AP", rating: 1, multiplier: 0.75 }`
  },
  {
    name: "finalCostMultiplier",
    label: "Final Cost Multiplier",
    arguments: ["weapon", "unit"],
    returnType: "number",
    hint: `A function ((weapon, unit) => object) that will be passed the weapon unit and returns a number by which the final cost of the weapon will be multiplied.`
  },
  {
    name: "initialise",
    label: "Initialise",
    arguments: ["weapon", "unit"],
    returnType: "void",
    hint: "A function ((weapon, unit) => void). Modify the weapon and/or unit before anything happens."
  }
  // {
  //   name: 'addQualityCost',
  //   label: 'Weapon quality cost modifier',
  //   arguments: ['weapon'],
  //   hint: ''
  // },
  // {
  //   name: 'weaponBaseCostMultiplier',
  //   label: 'Weapon base cost multiplier',
  //   arguments: ['weapon'],
  //   hint: 'A number by which to multiply the base cost, or a function that returns a number to multiply by. If a function, it will be passed the weapon. This happens just after the weapon\'s cost is multiplied by the unit\'s quality.'
  // },
];
